import { Button, VStack } from '@chakra-ui/react';

import { ADVISOR_PANEL_NEUTRAL_200 } from './advisor-panel-tokens';

export type AdvisorSuggestionChipProps = {
  text: string;
  /** When set, included in the payload for Kin ``route_use_case`` routing. */
  use_case?: string;
  onSend: (payload: { text: string; use_case?: string }) => void;
  disabled?: boolean;
  /** Chakra token or CSS color for hover border (and default hover text if `hoverTextColor` omitted). */
  hoverBorderColor?: string;
  hoverTextColor?: string;
};

export function AdvisorSuggestionChip({
  text,
  use_case,
  onSend,
  disabled,
  hoverBorderColor = 'teal.500',
  hoverTextColor,
}: AdvisorSuggestionChipProps) {
  const hoverColor = hoverTextColor ?? hoverBorderColor;
  return (
    <Button
      variant="outline"
      size="sm"
      w="100%"
      minH="45px"
      h="auto"
      py={1.5}
      px={2.5}
      justifyContent="flex-start"
      fontWeight="medium"
      fontSize="12px"
      lineHeight="18px"
      color="black"
      borderColor={ADVISOR_PANEL_NEUTRAL_200}
      borderRadius="4px"
      bg="white"
      _hover={{ borderColor: hoverBorderColor, color: hoverColor }}
      disabled={disabled}
      onClick={() => onSend({ text, use_case })}
    >
      {text}
    </Button>
  );
}

export type AdvisorFollowUpOptionInput = string | { id?: string; text: string; use_case?: string };

export type AdvisorFollowUpSuggestionsProps = {
  options: AdvisorFollowUpOptionInput[];
  onSelect: (option: { text: string; use_case?: string }) => void;
  disabled?: boolean;
  hoverBorderColor?: string;
  hoverTextColor?: string;
    maxW?: string;
};

export function AdvisorFollowUpSuggestions({
  options,
  onSelect,
  disabled,
  hoverBorderColor,
  hoverTextColor,
  maxW = '404px',
}: AdvisorFollowUpSuggestionsProps) {
  if (!options.length) {
    return null;
  }

  const normalizedOptions = options
    .map(option => {
      if (typeof option === 'string') {
        const t = option.trim();
        return t.length > 0 ? { text: t, use_case: undefined as string | undefined, key: undefined as string | undefined } : null;
      }
      const t = option.text.trim();
      if (!t.length) {
        return null;
      }
      const u =
        typeof option.use_case === 'string' && option.use_case.trim().length > 0
          ? option.use_case.trim()
          : undefined;
      const key = option.id && option.id.trim().length > 0 ? option.id.trim() : undefined;
      return { text: t, use_case: u, key };
    })
    .filter((option): option is NonNullable<typeof option> => option !== null);

  if (!normalizedOptions.length) {
    return null;
  }
  return (
    <VStack gap={2} w="100%" maxW={maxW} align="stretch" pt={1}>
      {normalizedOptions.map((option, idx) => (
        <AdvisorSuggestionChip
          key={option.key ?? `${option.text}-${idx}`}
          text={option.text}
          use_case={option.use_case}
          onSend={onSelect}
          disabled={disabled}
          hoverBorderColor={hoverBorderColor}
          hoverTextColor={hoverTextColor}
        />
      ))}
    </VStack>
  );
}
